<template>
  <div class="bg-[hsl(var(--background))] min-h-screen">
    <PageHeader
      :title="`WELCOME BACK, ${firstName.toUpperCase()}`"
      :subtitle="activeTeamName || 'No team selected'"
    />

    <div class="max-w-5xl mx-auto px-4 py-7 space-y-8">

      <!-- Team switcher (multi-team users) -->
      <div v-if="myTeams.length > 1">
        <p class="section-heading">Active Team</p>
        <div class="flex flex-wrap gap-2">
          <button
            v-for="team in myTeams"
            :key="team.team_id"
            @click="authStore.switchTeam(team.team_id)"
            :class="[
              'px-4 py-2 rounded-full text-sm font-semibold transition-all duration-200',
              authStore.activeTeamId === team.team_id
                ? 'bg-mlb-navy text-white shadow-[var(--shadow-glow-navy)] scale-[1.02]'
                : 'bg-white dark:bg-[#0f1f3d] text-gray-600 dark:text-slate-300 border border-gray-200 dark:border-[#1e3a5f] hover:border-mlb-navy/40 dark:hover:border-blue-500/50 hover:shadow-sm'
            ]"
          >
            {{ team.team_name }}
          </button>
        </div>
      </div>

      <!-- Quick actions (Coach / Admin) -->
      <div v-if="authStore.isAdmin || isActiveTeamCoach">
        <p class="section-heading">Quick Actions</p>
        <div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <router-link
            v-if="authStore.activeTeamId"
            :to="`/teams/${authStore.activeTeamId}/invites`"
            class="action-tile group"
          >
            <div class="action-icon bg-mlb-red/10 dark:bg-mlb-red/20 group-hover:bg-mlb-red/20 dark:group-hover:bg-mlb-red/30">
              <UserPlus class="w-5 h-5 text-mlb-red" />
            </div>
            <span class="action-label">Invite Players</span>
          </router-link>

          <router-link
            v-if="authStore.activeTeamId"
            :to="`/teams/${authStore.activeTeamId}`"
            class="action-tile group"
          >
            <div class="action-icon bg-emerald-500/10 dark:bg-emerald-500/20 group-hover:bg-emerald-500/20">
              <Users class="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <span class="action-label">Team Page</span>
          </router-link>

          <router-link
            v-if="authStore.isAdmin"
            to="/admin/seasons"
            class="action-tile group"
          >
            <div class="action-icon bg-mlb-gold/10 dark:bg-mlb-gold/20 group-hover:bg-mlb-gold/20">
              <Calendar class="w-5 h-5 text-mlb-gold dark:text-mlb-gold-light" />
            </div>
            <span class="action-label">Manage Seasons</span>
          </router-link>

          <router-link
            v-if="authStore.isAdmin"
            to="/admin/users"
            class="action-tile group"
          >
            <div class="action-icon bg-blue-500/10 dark:bg-blue-500/20 group-hover:bg-blue-500/20">
              <UserCog class="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <span class="action-label">User Management</span>
          </router-link>
        </div>
      </div>

      <!-- Upcoming Games -->
      <div v-if="authStore.activeTeamId">
        <div class="flex items-center justify-between mb-3">
          <p class="section-heading !mb-0">Upcoming Games</p>
          <router-link
            :to="`/teams/${authStore.activeTeamId}`"
            class="flex items-center gap-1 text-xs font-semibold text-mlb-navy dark:text-blue-400 hover:text-mlb-red dark:hover:text-mlb-red transition-colors"
          >
            Full schedule
            <ArrowRight class="w-3.5 h-3.5" />
          </router-link>
        </div>

        <!-- Loading -->
        <div v-if="loadingGames" class="space-y-2">
          <div v-for="i in 3" :key="i" class="h-16 rounded-xl shimmer" />
        </div>

        <!-- Empty -->
        <div
          v-else-if="upcomingGames.length === 0"
          class="card flex flex-col items-center justify-center py-12 text-center"
        >
          <div class="w-12 h-12 rounded-full bg-slate-100 dark:bg-[#1e3a5f]/50 flex items-center justify-center mb-3">
            <CalendarX class="w-6 h-6 text-slate-400 dark:text-slate-500" />
          </div>
          <p class="text-sm font-semibold text-gray-900 dark:text-slate-100">No upcoming games</p>
          <p class="text-xs text-gray-500 dark:text-slate-400 mt-1">Check back when your schedule is set.</p>
        </div>

        <!-- Games list -->
        <div v-else class="space-y-2">
          <div
            v-for="game in upcomingGames"
            :key="game.game_id"
            class="game-card group"
          >
            <!-- Date badge -->
            <div class="game-date-badge shrink-0">
              <span class="game-date-month">{{ formatMonth(game.game_date) }}</span>
              <span class="game-date-day">{{ formatDay(game.game_date) }}</span>
            </div>

            <div class="flex-1 min-w-0">
              <p class="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <span class="text-[10px] font-bold uppercase tracking-widest text-mlb-navy/50 dark:text-blue-400/60" style="font-family: var(--font-display)">{{ isHomeGame(game) ? 'VS' : '@' }}</span>
                {{ opponentFor(game) }}
                <span class="text-[10px] font-semibold uppercase text-gray-400 dark:text-slate-500">({{ isHomeGame(game) ? 'Home' : 'Away' }})</span>
              </p>
              <p class="text-xs text-gray-500 dark:text-slate-400 mt-0.5 flex items-center gap-1.5">
                <Clock class="w-3 h-3" />
                {{ formatTime(game.game_date) }}
                <span v-if="game.venue_name" class="flex items-center gap-1">
                  <span>·</span>
                  <MapPin class="w-3 h-3" />
                  {{ game.venue_name }}
                </span>
              </p>
            </div>

            <div class="flex items-center gap-3 shrink-0">
              <!-- Score display -->
              <div v-if="game.home_score != null" class="text-center">
                <div class="text-lg font-black text-gray-800 dark:text-white leading-none" style="font-family: var(--font-display)">
                  {{ game.home_score }}<span class="text-gray-400 dark:text-slate-500 mx-0.5">–</span>{{ game.away_score }}
                </div>
                <div class="text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-slate-500" style="font-family: var(--font-display)">Final</div>
              </div>
              <span v-else class="badge-navy text-[10px]">SCHEDULED</span>

              <router-link
                v-if="authStore.canManageTeam(authStore.activeTeamId!)"
                :to="`/teams/${authStore.activeTeamId}/games/${game.game_id}/lineup`"
                class="flex items-center gap-1 text-xs font-semibold text-mlb-navy dark:text-blue-400 hover:text-mlb-red dark:hover:text-mlb-red transition-colors opacity-0 group-hover:opacity-100"
              >
                Lineup
                <ArrowRight class="w-3 h-3" />
              </router-link>
            </div>
          </div>
        </div>
      </div>

      <!-- All Teams (Admin) -->
      <div v-if="authStore.isAdmin && allTeams.length > 0">
        <p class="section-heading">All Teams</p>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <router-link
            v-for="team in allTeams"
            :key="team.team_id"
            :to="`/teams/${team.team_id}`"
            class="card px-5 py-4 hover:border-mlb-navy/40 dark:hover:border-blue-500/40 transition-all group flex items-center justify-between"
          >
            <div class="flex items-center gap-3">
              <div class="w-9 h-9 rounded-full bg-mlb-navy/10 dark:bg-mlb-navy/30 flex items-center justify-center shrink-0">
                <Shield class="w-4 h-4 text-mlb-navy dark:text-blue-400" />
              </div>
              <div>
                <p class="text-sm font-semibold text-gray-900 dark:text-slate-100">{{ team.team_name }}</p>
                <p v-if="team.venue_name" class="text-xs text-gray-500 dark:text-slate-400 flex items-center gap-1">
                  <MapPin class="w-3 h-3" />
                  {{ team.venue_name }}
                </p>
              </div>
            </div>
            <ChevronRight class="w-4 h-4 text-gray-300 dark:text-slate-600 group-hover:text-mlb-navy dark:group-hover:text-blue-400 transition-colors" />
          </router-link>
        </div>
      </div>

      <!-- No team state -->
      <div
        v-if="!authStore.activeTeamId && !authStore.isAdmin"
        class="card py-16 text-center"
      >
        <div class="w-16 h-16 mx-auto rounded-full bg-mlb-navy/8 dark:bg-mlb-navy/30 flex items-center justify-center mb-4">
          <Users class="w-8 h-8 text-slate-300 dark:text-slate-600" />
        </div>
        <p class="text-base font-bold text-gray-900 dark:text-slate-100" style="font-family: var(--font-display)">NO TEAM YET</p>
        <p class="text-sm text-gray-500 dark:text-slate-400 mt-1">Ask your coach to send you an invite link.</p>
      </div>

    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import {
  Users, UserPlus, Calendar, CalendarX, UserCog,
  ArrowRight, ChevronRight, MapPin, Clock, Shield
} from 'lucide-vue-next'
import { useAuthStore } from '@/stores/auth'
import { apiClient } from '@/api/client'
import PageHeader from '@/components/common/PageHeader.vue'

const authStore = useAuthStore()

const loadingGames = ref(false)
const upcomingGames = ref<any[]>([])
const allTeams = ref<any[]>([])

const firstName = computed(() =>
  authStore.userInfo?.firstName || authStore.userInfo?.username || 'there'
)

const myTeams = computed(() => {
  const seen = new Set<number>()
  return authStore.userPermissions
    .filter(p => { if (seen.has(p.team_id)) return false; seen.add(p.team_id); return true })
    .map(p => ({ team_id: p.team_id, team_name: p.team_name }))
})

const activeTeamName = computed(() => {
  if (!authStore.activeTeamId) return ''
  const t = myTeams.value.find(t => t.team_id === authStore.activeTeamId)
  return t?.team_name || allTeams.value.find(t => t.team_id === authStore.activeTeamId)?.team_name || ''
})

const isActiveTeamCoach = computed(() =>
  authStore.activeTeamId ? authStore.canManageTeam(authStore.activeTeamId) : false
)

onMounted(async () => {
  await authStore.refreshUserInfo()
  if (authStore.isAdmin) loadAllTeams()
})

// Reload the schedule whenever the active team changes (pills or header
// switcher) so the list never shows a previous team's games.
watch(
  () => authStore.activeTeamId,
  teamId => {
    upcomingGames.value = []
    if (teamId) loadGames()
  },
  { immediate: true }
)

// Which side the active team plays, and who they face.
function isHomeGame(game: any): boolean {
  return String(game.home_team_id) === String(authStore.activeTeamId)
}
function opponentFor(game: any): string {
  return (isHomeGame(game) ? game.away_team : game.home_team) || 'TBD'
}

async function loadGames() {
  const teamId = authStore.activeTeamId
  if (!teamId) return
  loadingGames.value = true
  try {
    const res = await apiClient.get(`/games?team_id=${teamId}`)
    // Ignore stale responses if the user switched teams mid-flight.
    if (authStore.activeTeamId !== teamId) return
    const now = Date.now()
    upcomingGames.value = (res.data || [])
      .filter((g: any) => new Date(g.game_date).getTime() >= now - 86400000)
      .sort((a: any, b: any) => new Date(a.game_date).getTime() - new Date(b.game_date).getTime())
      .slice(0, 5)
  } catch { /* non-fatal */ }
  finally { loadingGames.value = false }
}

async function loadAllTeams() {
  try {
    const res = await apiClient.get('/teams')
    allTeams.value = res.data || []
  } catch { /* non-fatal */ }
}

function formatMonth(d: string) {
  return new Date(d).toLocaleDateString('en-US', { month: 'short' }).toUpperCase()
}
function formatDay(d: string) {
  return new Date(d).getDate()
}
function formatTime(d: string) {
  return new Date(d).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
}
</script>

<style scoped>
.action-tile {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.625rem;
  padding: 1rem;
  border-radius: 0.75rem;
  border-width: 1px;
  border-style: solid;
  background: white;
  border-color: #e5e7eb;
  transition: all 0.2s;
}

.dark .action-tile {
  background: #0f1f3d;
  border-color: #1e3a5f;
}

.action-tile:hover {
  border-color: rgba(0, 45, 98, 0.3);
  box-shadow: var(--shadow-card-hover);
}

.dark .action-tile:hover {
  border-color: rgba(59, 130, 246, 0.4);
}

.action-tile:active {
  transform: scale(0.98);
}

.action-icon {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;
}

.action-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #1f2937;
  line-height: 1.25;
}

.dark .action-label {
  color: #f1f5f9;
}

.game-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.875rem 1rem;
  border-radius: 0.75rem;
  border-width: 1px;
  border-style: solid;
  background: white;
  border-color: #f3f4f6;
  transition: all 0.2s;
}

.dark .game-card {
  background: #0f1f3d;
  border-color: #1e3a5f;
}

.game-card:hover {
  border-color: rgba(0, 45, 98, 0.2);
  box-shadow: 0 4px 16px rgba(0,0,0,0.08);
}

.dark .game-card:hover {
  border-color: rgba(59, 130, 246, 0.3);
}

.game-date-badge {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 0.75rem;
  background: rgba(0, 45, 98, 0.08);
  border: 1px solid rgba(0, 45, 98, 0.1);
  flex-shrink: 0;
}

.dark .game-date-badge {
  background: rgba(0, 45, 98, 0.3);
  border-color: rgba(26, 68, 128, 0.2);
}

.game-date-month {
  font-size: 9px;
  font-weight: 700;
  color: rgba(0, 45, 98, 0.6);
  line-height: 1;
  font-family: var(--font-display);
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.dark .game-date-month {
  color: rgba(96, 165, 250, 0.6);
}

.game-date-day {
  font-size: 1rem;
  font-weight: 900;
  color: #002D62;
  line-height: 1;
  margin-top: 0.125rem;
  font-family: var(--font-display);
}

.dark .game-date-day {
  color: white;
}
</style>
